/**
 * MockMetadataService — seeded with ARM-representative fixture data.
 *
 * The fixture reflects a realistic dev-source vs dev-target diff for
 * the ARM Revenue Cloud metadata working set:
 *   - 2 ApexClass changes, 1 added
 *   - 3 CustomField changes on Attribute__c (the canonical ARM object)
 *   - 1 Flow added, 1 unchanged
 *   - 1 ValidationRule unchanged
 *
 * getDiffDetail returns plausible XML / JSON body pairs for the
 * changed Attribute__c fields so the line diff view is exercisable.
 */

import type { IMetadataService, GitDeployStatus, ComponentSource } from './IMetadataService.interface'
import type {
  MetadataSnapshot,
  MetadataComparison,
  ComponentDiffDetail,
  MetadataCapability,
  MetadataSelector,
  ComponentListing,
  MetadataTypeInfo,
  CompareStreamEvent,
  TypeRunCounts,
  ComponentChangeKind,
} from '@stride/shared'
import type { CompareSource } from '@stride/shared'

// ── Demo fixture ──────────────────────────────────────────────────────────────

const SNAP_LEFT: MetadataSnapshot = {
  id:             'snap-demo-left-0000-0000-000000000001',
  sourceKind:     'org',
  sourceRef:      'conn-demo-sf-001',
  types:          ['ApexClass', 'CustomField', 'Flow', 'ValidationRule'],
  componentCount: 10,
  blobPath:       'snap-demo-left-0000-0000-000000000001',
  createdAt:      '2026-06-01T08:00:00.000Z',
}

const SNAP_RIGHT: MetadataSnapshot = {
  id:             'snap-demo-right-000-0000-000000000002',
  sourceKind:     'org',
  sourceRef:      'conn-demo-sf-002',
  types:          ['ApexClass', 'CustomField', 'Flow', 'ValidationRule'],
  componentCount: 11,
  blobPath:       'snap-demo-right-000-0000-000000000002',
  createdAt:      '2026-06-01T08:05:00.000Z',
}

const DEMO_COMPARISON: MetadataComparison = {
  id:        'cmp-demo-0000-0000-0000-000000000003',
  left:      { kind: 'org', ref: 'conn-demo-sf-001', snapshotId: SNAP_LEFT.id  },
  right:     { kind: 'org', ref: 'conn-demo-sf-002', snapshotId: SNAP_RIGHT.id },
  // Summary reflects ltr default: left-only=added, right-only=deleted
  summary:   { added: 3, changed: 3, deleted: 1, unchanged: 5 },
  diffs: [
    // ApexClass — both: changed
    { type: 'ApexClass', fullName: 'ARMPricingEngine',         presentIn: 'both',  change: 'changed'   },
    { type: 'ApexClass', fullName: 'ARMProductClassification', presentIn: 'both',  change: 'changed'   },
    // ApexClass — left-only (in dev source, not yet in prod): added in ltr
    { type: 'ApexClass', fullName: 'ARMUsageProcessor',        presentIn: 'left',  change: 'added'     },
    // ApexClass — both: unchanged
    { type: 'ApexClass', fullName: 'ARMBaseHandler',           presentIn: 'both',  change: 'unchanged' },
    // ApexClass — right-only (exists in prod but removed from dev): deleted in ltr
    { type: 'ApexClass', fullName: 'ARMDeprecatedHelper',      presentIn: 'right', change: 'deleted'   },
    // CustomField — both: changed
    { type: 'CustomField', fullName: 'Attribute__c.CalcValue__c',   presentIn: 'both',  change: 'changed'   },
    { type: 'CustomField', fullName: 'Attribute__c.PriceSource__c', presentIn: 'both',  change: 'changed'   },
    // CustomField — left-only: added in ltr
    { type: 'CustomField', fullName: 'Attribute__c.NewFlag__c',     presentIn: 'left',  change: 'added'     },
    // CustomField — both: unchanged
    { type: 'CustomField', fullName: 'Attribute__c.GlobalKey__c',   presentIn: 'both',  change: 'unchanged' },
    // Flow — both: unchanged
    { type: 'Flow', fullName: 'ARM_Order_Enrichment',  presentIn: 'both', change: 'unchanged' },
    // ValidationRule — both: unchanged
    { type: 'ValidationRule', fullName: 'Attribute__c.RequireCalcValue', presentIn: 'both', change: 'unchanged' },
  ],
  createdAt: '2026-06-01T08:10:00.000Z',
}

// Plausible XML bodies for Attribute__c.CalcValue__c
const CALC_LEFT = `<?xml version="1.0" encoding="UTF-8"?>
<CustomField xmlns="http://soap.sforce.com/2006/04/metadata">
    <fullName>CalcValue__c</fullName>
    <externalId>false</externalId>
    <label>Calc Value</label>
    <precision>18</precision>
    <scale>2</scale>
    <trackHistory>false</trackHistory>
    <type>Currency</type>
</CustomField>`

const CALC_RIGHT = `<?xml version="1.0" encoding="UTF-8"?>
<CustomField xmlns="http://soap.sforce.com/2006/04/metadata">
    <fullName>CalcValue__c</fullName>
    <externalId>false</externalId>
    <label>Calculated Value</label>
    <precision>18</precision>
    <scale>4</scale>
    <trackHistory>true</trackHistory>
    <type>Currency</type>
</CustomField>`

const PRICE_LEFT = `<?xml version="1.0" encoding="UTF-8"?>
<CustomField xmlns="http://soap.sforce.com/2006/04/metadata">
    <fullName>PriceSource__c</fullName>
    <label>Price Source</label>
    <length>255</length>
    <type>Text</type>
</CustomField>`

const PRICE_RIGHT = `<?xml version="1.0" encoding="UTF-8"?>
<CustomField xmlns="http://soap.sforce.com/2006/04/metadata">
    <fullName>PriceSource__c</fullName>
    <label>Price Source</label>
    <length>100</length>
    <required>true</required>
    <type>Text</type>
</CustomField>`

// ── Seeded component listings (for scope picker) ──────────────────────────────

const MOCK_LISTINGS: ComponentListing[] = [
  {
    type: 'ApexClass',
    members: [
      { fullName: 'ARMPricingEngine',          lastModifiedAt: '2026-05-15T09:00:00.000Z', lastModifiedBy: 'Admin' },
      { fullName: 'ARMProductClassification',  lastModifiedAt: '2026-05-10T14:00:00.000Z', lastModifiedBy: 'Dev'   },
      { fullName: 'ARMUsageProcessor',         lastModifiedAt: '2026-05-20T11:30:00.000Z', lastModifiedBy: 'Dev'   },
      { fullName: 'ARMBaseHandler',            lastModifiedAt: '2026-04-01T08:00:00.000Z', lastModifiedBy: 'Admin' },
      { fullName: 'ARMOrderEnricher',          lastModifiedAt: '2026-04-15T16:00:00.000Z', lastModifiedBy: 'Dev'   },
    ],
  },
  {
    type: 'CustomField',
    members: [
      { fullName: 'Attribute__c.CalcValue__c',   lastModifiedAt: '2026-05-18T10:00:00.000Z', lastModifiedBy: 'Admin' },
      { fullName: 'Attribute__c.PriceSource__c', lastModifiedAt: '2026-05-12T09:00:00.000Z', lastModifiedBy: 'Dev'   },
      { fullName: 'Attribute__c.NewFlag__c',     lastModifiedAt: '2026-05-20T15:00:00.000Z', lastModifiedBy: 'Dev'   },
      { fullName: 'Attribute__c.GlobalKey__c',   lastModifiedAt: '2026-03-01T08:00:00.000Z', lastModifiedBy: 'Admin' },
    ],
  },
  {
    type: 'Flow',
    members: [
      { fullName: 'ARM_Order_Enrichment', lastModifiedAt: '2026-04-10T10:00:00.000Z', lastModifiedBy: 'Admin' },
    ],
  },
  {
    type: 'ValidationRule',
    members: [
      { fullName: 'Attribute__c.RequireCalcValue', lastModifiedAt: '2026-03-15T08:00:00.000Z', lastModifiedBy: 'Admin' },
    ],
  },
]

const MOCK_LISTING_MAP = new Map<string, ComponentListing>(
  MOCK_LISTINGS.map((l) => [l.type, l]),
)

// Branch-source listings — names only, no lastModifiedAt (git trees carry no
// per-file mtime). A small, type-diverse set so the branch source renders.
const MOCK_BRANCH_LISTINGS: ComponentListing[] = [
  {
    type: 'ApexClass',
    members: [
      { fullName: 'ARMPricingEngine' },
      { fullName: 'ARMProductClassification' },
    ],
  },
  {
    type: 'CustomField',
    members: [
      { fullName: 'Attribute__c.CalcValue__c' },
      { fullName: 'Attribute__c.PriceSource__c' },
    ],
  },
  {
    type: 'Flow',
    members: [{ fullName: 'ARM_Order_Enrichment' }],
  },
]

// ── Seeded type descriptors (describeMetadata mock) ───────────────────────────

export const MOCK_DESCRIBE_TYPES: MetadataTypeInfo[] = [
  // Apex & code
  { xmlName: 'ApexClass',     directoryName: 'classes',      inFolder: false, category: 'Apex & code'      },
  { xmlName: 'ApexTrigger',   directoryName: 'triggers',     inFolder: false, category: 'Apex & code'      },
  { xmlName: 'ApexPage',      directoryName: 'pages',        inFolder: false, category: 'Apex & code'      },
  { xmlName: 'ApexComponent', directoryName: 'components',   inFolder: false, category: 'Apex & code'      },
  { xmlName: 'StaticResource',directoryName: 'staticresources', inFolder: false, category: 'Apex & code'   },
  // Automation
  { xmlName: 'Flow',             directoryName: 'flows',         inFolder: false, category: 'Automation'   },
  { xmlName: 'WorkflowRule',     directoryName: 'workflows',     inFolder: false, category: 'Automation'   },
  { xmlName: 'ApprovalProcess',  directoryName: 'approvalProcesses', inFolder: false, category: 'Automation' },
  { xmlName: 'WorkflowFieldUpdate', directoryName: 'workflows',  inFolder: false, category: 'Automation'   },
  // Objects & fields
  {
    xmlName: 'CustomObject',  directoryName: 'objects',      inFolder: false, category: 'Objects & fields',
    childXmlNames: ['CustomField', 'ValidationRule', 'RecordType', 'FieldSet', 'ListView'],
  },
  { xmlName: 'CustomField',   directoryName: 'objects',      inFolder: false, category: 'Objects & fields' },
  { xmlName: 'ValidationRule',directoryName: 'objects',      inFolder: false, category: 'Objects & fields' },
  { xmlName: 'RecordType',    directoryName: 'objects',      inFolder: false, category: 'Objects & fields' },
  { xmlName: 'FieldSet',      directoryName: 'objects',      inFolder: false, category: 'Objects & fields' },
  { xmlName: 'ListView',      directoryName: 'objects',      inFolder: false, category: 'Objects & fields' },
  // User interface
  { xmlName: 'Layout',        directoryName: 'layouts',      inFolder: false, category: 'User interface'   },
  { xmlName: 'LightningComponentBundle', directoryName: 'lwc', inFolder: false, category: 'User interface' },
  { xmlName: 'FlexiPage',     directoryName: 'flexipages',   inFolder: false, category: 'User interface'   },
  // Security & access
  { xmlName: 'PermissionSet', directoryName: 'permissionsets', inFolder: false, category: 'Security & access' },
  { xmlName: 'Profile',       directoryName: 'profiles',     inFolder: true,  category: 'Security & access' },
  { xmlName: 'CustomPermission', directoryName: 'customPermissions', inFolder: false, category: 'Security & access' },
  // OmniStudio
  { xmlName: 'OmniUiCard',    directoryName: 'omniUiCard',   inFolder: false, category: 'OmniStudio'       },
  { xmlName: 'OmniScript',    directoryName: 'omniScript',   inFolder: false, category: 'OmniStudio'       },
  { xmlName: 'OmniDataTransform', directoryName: 'omniDataTransform', inFolder: false, category: 'OmniStudio' },
  // Other
  { xmlName: 'CustomLabel',   directoryName: 'labels',       inFolder: false, category: 'Other'            },
]

// ── MockMetadataService ───────────────────────────────────────────────────────

export class MockMetadataService implements IMetadataService {
  private readonly snapshots = new Map<string, MetadataSnapshot>([
    [SNAP_LEFT.id,  SNAP_LEFT ],
    [SNAP_RIGHT.id, SNAP_RIGHT],
  ])

  private readonly comparisons = new Map<string, MetadataComparison>([
    [DEMO_COMPARISON.id, DEMO_COMPARISON],
  ])

  capabilities(): MetadataCapability[] {
    return ['retrieve', 'compare', 'partial-refresh']
  }

  describeTypes(_connectionId: string): Promise<MetadataTypeInfo[]> {
    return Promise.resolve([...MOCK_DESCRIBE_TYPES])
  }

  retrieve(_connectionId: string, types: string[]): Promise<MetadataSnapshot> {
    const snap: MetadataSnapshot = {
      id:             globalThis.crypto.randomUUID(),
      sourceKind:     'org',
      sourceRef:      _connectionId,
      types,
      componentCount: 0,
      blobPath:       '',
      createdAt:      new Date().toISOString(),
    }
    this.snapshots.set(snap.id, snap)
    return Promise.resolve({ ...snap })
  }

  listSnapshots(sourceRef?: string): Promise<MetadataSnapshot[]> {
    const all = [...this.snapshots.values()]
    if (sourceRef) return Promise.resolve(all.filter((s) => s.sourceRef === sourceRef))
    return Promise.resolve(all)
  }

  listComponents(_connectionId: string, types: string[]): Promise<ComponentListing[]> {
    return Promise.resolve(
      types.map((type) => MOCK_LISTING_MAP.get(type) ?? { type, members: [] }),
    )
  }

  listBranchComponents(
    _connectionId: string,
    _repo:         string,
    _branch:       string,
  ): Promise<ComponentListing[]> {
    // Branch listings carry no lastModifiedAt (the git tree has no per-file
    // mtime) — mirrors the real body-less branch-components route.
    return Promise.resolve(MOCK_BRANCH_LISTINGS.map((l) => ({
      type:    l.type,
      members: l.members.map((m) => ({ fullName: m.fullName })),
    })))
  }

  compare(left: CompareSource, right: CompareSource, _selectors?: MetadataSelector[]): Promise<MetadataComparison> {
    // Return the demo comparison if both sides match demo connections
    const leftRef  = 'connectionId' in left  ? left.connectionId  : ('snapshotId' in left  ? left.snapshotId  : '')
    const rightRef = 'connectionId' in right ? right.connectionId : ('snapshotId' in right ? right.snapshotId : '')

    if (
      (leftRef  === 'conn-demo-sf-001' || leftRef  === SNAP_LEFT.id)  &&
      (rightRef === 'conn-demo-sf-002' || rightRef === SNAP_RIGHT.id)
    ) {
      return Promise.resolve({ ...DEMO_COMPARISON })
    }

    // Generic empty comparison for any other pair
    const cmp: MetadataComparison = {
      id:        globalThis.crypto.randomUUID(),
      left:  { kind: left.kind  as 'org' | 'branch', ref: leftRef,  snapshotId: SNAP_LEFT.id  },
      right: { kind: right.kind as 'org' | 'branch', ref: rightRef, snapshotId: SNAP_RIGHT.id },
      summary:   { added: 0, changed: 0, deleted: 0, unchanged: 0 },
      diffs:     [],
      createdAt: new Date().toISOString(),
    }
    this.comparisons.set(cmp.id, cmp)
    return Promise.resolve({ ...cmp })
  }

  getDiffDetail(
    comparisonId: string,
    type:         string,
    fullName:     string,
  ): Promise<ComponentDiffDetail> {
    const comparison = this.comparisons.get(comparisonId) ?? DEMO_COMPARISON
    const diff = comparison.diffs.find((d) => d.type === type && d.fullName === fullName)
    const change    = diff?.change    ?? 'unchanged'
    const presentIn = diff?.presentIn ?? 'both'

    // Return seeded bodies for the two showcase components
    if (type === 'CustomField' && fullName === 'Attribute__c.CalcValue__c') {
      return Promise.resolve({
        type, fullName, presentIn, change,
        leftBody:  CALC_LEFT,
        rightBody: CALC_RIGHT,
      })
    }
    if (type === 'CustomField' && fullName === 'Attribute__c.PriceSource__c') {
      return Promise.resolve({
        type, fullName, presentIn, change,
        leftBody:  PRICE_LEFT,
        rightBody: PRICE_RIGHT,
      })
    }

    // Generic placeholder — one-sided components get one body only
    const placeholder = `<!-- ${type}: ${fullName} —body not seeded in mock— -->`
    const detail: ComponentDiffDetail = { type, fullName, presentIn, change }
    if (presentIn !== 'right') Object.assign(detail, { leftBody:  placeholder })
    if (presentIn !== 'left')  Object.assign(detail, { rightBody: placeholder })
    return Promise.resolve(detail)
  }

  getComponentSource(
    _source:  CompareSource,
    type:     string,
    fullName: string,
  ): Promise<ComponentSource> {
    // Reuse the seeded showcase bodies so the standalone View looks identical to
    // the compare-side View; fall back to a placeholder for everything else.
    if (type === 'CustomField' && fullName === 'Attribute__c.CalcValue__c') {
      return Promise.resolve({ type, fullName, body: CALC_RIGHT })
    }
    if (type === 'CustomField' && fullName === 'Attribute__c.PriceSource__c') {
      return Promise.resolve({ type, fullName, body: PRICE_RIGHT })
    }
    return Promise.resolve({ type, fullName, body: `<!-- ${type}: ${fullName} —body not seeded in mock— -->` })
  }

  refreshType(comparisonId: string, _type: string): Promise<MetadataComparison> {
    const cmp = this.comparisons.get(comparisonId) ?? DEMO_COMPARISON
    return Promise.resolve({ ...cmp, createdAt: new Date().toISOString() })
  }

  // ── FX2 Cherry-pick deploy ────────────────────────────────────────────────

  deployComponents(_params: {
    comparisonId:        string
    componentKeys:       string[]
    sourceConnectionId:  string
    targetConnectionId:  string
  }): Promise<{ deployRunId: string }> {
    // Return a stable demo run ID — MockEngineAdapter shows a simulated run
    return Promise.resolve({ deployRunId: 'mock-metadata-deploy-demo' })
  }

  deployComponentsDirect(_params: {
    components:          Array<{ type: string; fullName: string; changeKind?: ComponentChangeKind }>
    sourceConnectionId:  string
    targetConnectionId:  string
    mode?:               'deploy' | 'validate'
    destructiveAck?:     boolean
  }): Promise<{ deployRunId: string }> {
    return Promise.resolve({ deployRunId: 'mock-metadata-deploy-direct-demo' })
  }

  // ── FX3 Git-backed deploy ─────────────────────────────────────────────────

  deployViaGit(params: {
    comparisonId:        string
    componentKeys:       string[]
    sourceConnectionId:  string
    targetConnectionId:  string
    gitConnectionId:     string
    repo:                string
    commitMessage?:      string
    prTitle?:            string
    prBody?:             string
  }): Promise<{
    pullRequestUrl: string
    deployRunId:    string
    branch:         string
    workingBranch:  string
  }> {
    const [owner = 'o', name = 'r'] = params.repo.split('/')
    return Promise.resolve({
      pullRequestUrl: `https://github.com/${owner}/${name}/pull/1`,
      deployRunId:    'run-git-mock',
      branch:         'org/dev-target',
      workingBranch:  `deploy/${params.comparisonId.slice(0, 8)}`,
    })
  }

  // ── Brief 3 — stepped deploy (commit + branch-source deploy) ──────────────

  commitSelectionToBranch(params: {
    comparisonId:    string
    componentKeys:   string[]
    repo:            string
    branch:          string
    gitConnectionId: string
    commitMessage?:  string
    destructiveAck?: boolean
  }): Promise<{ fileCount: number; warnings: string[]; commitSha?: string }> {
    // Deterministic canned result — one file per selected key, no warnings.
    void params
    return Promise.resolve({
      fileCount: params.componentKeys.length,
      warnings:  [],
      commitSha: 'mock-commit-sha',
    })
  }

  deployFromBranch(params: {
    repo:               string
    branch:             string
    gitConnectionId:    string
    targetConnectionId: string
    testLevel?:         'NoTestRun' | 'RunLocalTests' | 'RunAllTestsInOrg'
  }): Promise<{ deployRunId: string }> {
    void params
    return Promise.resolve({ deployRunId: 'mock-deploy-branch' })
  }

  // ── FX3 Stage 2 — deploy-on-merge ─────────────────────────────────────────

  getGitDeployStatus(runId: string): Promise<GitDeployStatus> {
    return Promise.resolve({
      runId,
      status:             'paused',
      pullRequestUrl:     'https://github.com/o/r/pull/1',
      prNumber:           1,
      repo:               'o/r',
      orgBranch:          'org/dev-target',
      targetConnectionId: 'conn-demo-sf-002',
      merged:             false,
    })
  }

  triggerGitDeploy(runId: string): Promise<{ deployRunId: string }> {
    return Promise.resolve({ deployRunId: runId })
  }

  validateComponents(_params: {
    comparisonId:        string
    componentKeys:       string[]
    sourceConnectionId:  string
    targetConnectionId:  string
  }): Promise<{ deployRunId: string }> {
    return Promise.resolve({ deployRunId: 'run-validate-mock' })
  }

  captureFromCompare(
    _comparisonId: string,
    req: { type: string; fullName: string; gitConnectionId: string; repo: string; branch: string; fromBranch?: string; commitMessage?: string },
  ): Promise<{ type: string; fullName: string; branch: string; commitSha: string; paths: string[] }> {
    return Promise.resolve({ type: req.type, fullName: req.fullName, branch: req.branch, commitSha: 'mock-sha', paths: [`force-app/main/default/decisionMatrixDefinitions/${req.fullName}.decisionMatrixDefinition-meta.xml`] })
  }

  // ── F4 Streaming ──────────────────────────────────────────────────────────

  startCompare(left: CompareSource, right: CompareSource, selectors?: MetadataSelector[]): Promise<{ comparisonId: string }> {
    const leftRef  = 'connectionId' in left  ? left.connectionId  : ('snapshotId' in left  ? left.snapshotId  : '')
    const rightRef = 'connectionId' in right ? right.connectionId : ('snapshotId' in right ? right.snapshotId : '')

    // Re-use the demo comparison for the canonical demo connection pair
    const isDemo =
      (leftRef  === 'conn-demo-sf-001' || leftRef  === SNAP_LEFT.id)  &&
      (rightRef === 'conn-demo-sf-002' || rightRef === SNAP_RIGHT.id)

    const cmp = isDemo ? DEMO_COMPARISON : (() => {
      const c: MetadataComparison = {
        id:        globalThis.crypto.randomUUID(),
        left:  { kind: left.kind  as 'org' | 'branch', ref: leftRef,  snapshotId: SNAP_LEFT.id  },
        right: { kind: right.kind as 'org' | 'branch', ref: rightRef, snapshotId: SNAP_RIGHT.id },
        summary:   { added: 0, changed: 0, deleted: 0, unchanged: 0 },
        diffs:     [],
        createdAt: new Date().toISOString(),
      }
      this.comparisons.set(c.id, c)
      return c
    })()

    void selectors

    return Promise.resolve({ comparisonId: cmp.id })
  }

  subscribeCompare(comparisonId: string, onEvent: (e: CompareStreamEvent) => void): () => void {
    const cmp = this.comparisons.get(comparisonId) ?? DEMO_COMPARISON

    // Stagger events via setTimeout so the UI sees the streaming progression
    // in the real browser.  Tests that need synchronous firing provide their
    // own service stub.
    const types = [...new Set(cmp.diffs.map((d) => d.type))]

    let cancelled = false
    let delay = 0

    for (const type of types) {
      const t = type
      setTimeout(() => {
        if (cancelled) return
        onEvent({ kind: 'type.queued', comparisonId, type: t })
      }, delay)
      delay += 40

      setTimeout(() => {
        if (cancelled) return
        onEvent({ kind: 'type.retrieving', comparisonId, type: t })
      }, delay)
      delay += 80

      setTimeout(() => {
        if (cancelled) return
        const typeDiffs = cmp.diffs.filter((d) => d.type === t)
        const counts = _summarise(typeDiffs)
        onEvent({ kind: 'type.done', comparisonId, type: t, diffs: typeDiffs, counts })
      }, delay)
      delay += 40
    }

    setTimeout(() => {
      if (cancelled) return
      onEvent({ kind: 'compare.done', comparisonId, summary: _summarise(cmp.diffs) })
    }, delay)

    return () => { cancelled = true }
  }
}

// ── Helper ────────────────────────────────────────────────────────────────────

function _summarise(diffs: MetadataComparison['diffs']): TypeRunCounts {
  const s: TypeRunCounts = { added: 0, changed: 0, deleted: 0, unchanged: 0 }
  for (const d of diffs) s[d.change]++
  return s
}
